/**
 * CustomerEditModal — admin edit of a customer's contact details.
 *
 * Scope (operator decision, 2026-06-09): NAME and PHONE only. Email is the
 * login identity and is intentionally read-only here — shown for context but
 * not editable. Works for registered passengers and phone-booking customers.
 */
import React, { useState } from "react";
import { X, User, Phone, Mail, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";

// Phone-booking customers carry a synthetic "<digits>@phone.local" email.
const isSyntheticEmail = (email) => !email || email.endsWith("@phone.local");

export const CustomerEditModal = ({ customer, onClose, onSave }) => {
  const [name, setName] = useState(customer.name || "");
  const [phone, setPhone] = useState(customer.phone || "");
  const [submitting, setSubmitting] = useState(false);

  const changed =
    name.trim() !== (customer.name || "").trim() ||
    phone.trim() !== (customer.phone || "").trim();
  const canSubmit = name.trim().length > 0 && changed && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSave(customer.id, { name: name.trim(), phone: phone.trim() });
      onClose();
    } catch {
      // toast handled by caller; keep modal open so the operator can retry
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="customer-edit-modal"
      onMouseDown={onClose}
    >
      <div
        className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto border border-border"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-white">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight flex items-center gap-2">
              <User className="w-4 h-4" /> Edit customer
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">Update name and phone number.</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1"
            data-testid="customer-edit-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="label-mini text-muted-foreground">Name</label>
            <div className="flex items-center px-3 py-2 border border-border bg-white mt-1">
              <User className="w-4 h-4 text-muted-foreground mr-2" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Customer name"
                className="flex-1 outline-none bg-transparent text-sm"
                data-testid="customer-edit-name-input"
              />
            </div>
          </div>

          <div>
            <label className="label-mini text-muted-foreground">Phone number</label>
            <div className="flex items-center px-3 py-2 border border-border bg-white mt-1">
              <Phone className="w-4 h-4 text-muted-foreground mr-2" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. 07700 900123"
                className="flex-1 outline-none bg-transparent text-sm"
                data-testid="customer-edit-phone-input"
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              UK format accepted (07…, +44…). Must be unique across customers.
            </p>
          </div>

          {/* Email shown for context — not editable (login identity). */}
          <div>
            <label className="label-mini text-muted-foreground">Email (read-only)</label>
            <div className="flex items-center px-3 py-2 border border-dashed border-border bg-secondary/40 mt-1">
              <Mail className="w-4 h-4 text-muted-foreground mr-2" />
              <span className="flex-1 text-sm text-muted-foreground" data-testid="customer-edit-email-readonly">
                {isSyntheticEmail(customer.email) ? "Phone booking (no login email)" : customer.email}
              </span>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} data-testid="customer-edit-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-[#002FA7] hover:bg-[#001f6e] text-white disabled:opacity-50"
            data-testid="customer-edit-save-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Saving…</>
            ) : (
              "Save changes"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerEditModal;
